/**
 * Direct-to-DeepSeek mode implementations of web_search and read_docs.
 *
 * These run when `DEEPSEEK_API_KEY` is set and the Codebuff backend is not
 * used. They replace the backend `/api/v1/web-search` and `/api/v1/docs-search`
 * calls with direct upstream calls:
 *
 *  - read_docs  → Context7 (free, no key required; CONTEXT7_API_KEY optional)
 *  - web_search → Serper Google Search when SERPER_API_KEY is set, otherwise
 *                 DuckDuckGo's Instant Answer API (free, no key required)
 *
 * Both return the same shape the backend facade returns so the tool handlers
 * and their tests are unchanged.
 */

import { fetchContext7LibraryDocumentation } from './context7-api'
import { withTimeout } from '@anvil/common/util/promise'

import type { Logger } from '@anvil/common/types/contracts/logger'

const FETCH_TIMEOUT_MS = 15_000
const SERPER_BASE_URL = 'https://google.serper.dev/search'
const DUCKDUCKGO_INSTANT_ANSWER_URL = 'https://api.duckduckgo.com/'

const tryParseJson = (text: string): unknown => {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

const getStringField = (value: unknown, key: string): string | undefined => {
  if (!value || typeof value !== 'object') return undefined
  const field = (value as Record<string, unknown>)[key]
  return typeof field === 'string' ? field : undefined
}

// ---------------------------------------------------------------------------
// web_search
// ---------------------------------------------------------------------------

/** Format a list of results into the plain-text `result` the tool returns. */
function formatSearchResults(results: Array<{
  title?: string
  link?: string
  snippet?: string
}>): string {
  if (results.length === 0) return ''
  return results
    .map((r, i) => {
      const parts = [`${i + 1}. ${r.title ?? '(untitled)'}`]
      if (r.link) parts.push(`   ${r.link}`)
      if (r.snippet) parts.push(`   ${r.snippet}`)
      return parts.join('\n')
    })
    .join('\n\n')
}

async function searchWithSerper(params: {
  query: string
  apiKey: string
  depth: 'standard' | 'deep'
  fetch: typeof globalThis.fetch
  logger: Logger
}): Promise<{ result?: string; error?: string }> {
  const { query, apiKey, depth, fetch, logger } = params
  try {
    const res = await withTimeout(
      fetch(SERPER_BASE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-KEY': apiKey,
        },
        body: JSON.stringify({
          q: query,
          num: depth === 'deep' ? 20 : 10,
          gl: 'us',
          hl: 'en',
        }),
      }),
      FETCH_TIMEOUT_MS,
      `Serper search timed out after ${FETCH_TIMEOUT_MS}ms`,
    )
    const text = await res.text()
    if (!res.ok) {
      logger.warn(
        { status: res.status, body: text.slice(0, 500), query },
        'Direct web search (Serper) request failed',
      )
      return { error: `Serper search failed: ${res.status} ${text.slice(0, 200)}` }
    }
    const json = tryParseJson(text)
    if (!json || typeof json !== 'object') {
      return { error: 'Serper search returned an invalid response' }
    }

    const results: Array<{ title?: string; link?: string; snippet?: string }> =
      []

    const answerBox = (json as Record<string, unknown>).answerBox
    if (answerBox && typeof answerBox === 'object') {
      const a = answerBox as Record<string, unknown>
      results.push({
        title: getStringField(a, 'title'),
        link: getStringField(a, 'link'),
        snippet: getStringField(a, 'snippet'),
      })
    }

    const knowledgeGraph = (json as Record<string, unknown>).knowledgeGraph
    if (knowledgeGraph && typeof knowledgeGraph === 'object') {
      const kg = knowledgeGraph as Record<string, unknown>
      results.push({
        title: getStringField(kg, 'title'),
        link: getStringField(kg, 'descriptionSource'),
        snippet: getStringField(kg, 'description'),
      })
    }

    const organic = (json as Record<string, unknown>).organic
    if (Array.isArray(organic)) {
      for (const item of organic) {
        if (!item || typeof item !== 'object') continue
        const rec = item as Record<string, unknown>
        results.push({
          title: getStringField(rec, 'title'),
          link: getStringField(rec, 'link'),
          snippet: getStringField(rec, 'snippet'),
        })
      }
    }

    const news = (json as Record<string, unknown>).news
    if (Array.isArray(news)) {
      for (const item of news) {
        if (!item || typeof item !== 'object') continue
        const rec = item as Record<string, unknown>
        results.push({
          title: getStringField(rec, 'title'),
          link: getStringField(rec, 'link'),
          snippet: getStringField(rec, 'snippet'),
        })
      }
    }

    const result = formatSearchResults(results)
    if (!result) {
      return { error: `No search results found for "${query}"` }
    }
    return { result }
  } catch (error) {
    logger.error(
      {
        error:
          error instanceof Error
            ? { name: error.name, message: error.message }
            : error,
        query,
      },
      'Direct web search (Serper) network error',
    )
    return { error: error instanceof Error ? error.message : 'Network error' }
  }
}

/** DuckDuckGo Instant Answer API — free, no key. Coverage is narrower than
 *  a full search engine (definitions, abstracts, related topics), but it
 *  works with zero setup so web_search is usable out of the box. */
async function searchWithDuckDuckGo(params: {
  query: string
  depth: 'standard' | 'deep'
  fetch: typeof globalThis.fetch
  logger: Logger
}): Promise<{ result?: string; error?: string }> {
  const { query, depth, fetch, logger } = params
  try {
    const url = new URL(DUCKDUCKGO_INSTANT_ANSWER_URL)
    url.searchParams.set('q', query)
    url.searchParams.set('format', 'json')
    url.searchParams.set('no_html', '1')
    url.searchParams.set('skip_disambig', '1')

    const res = await withTimeout(
      fetch(url, { headers: { 'User-Agent': 'codebuff-direct/1.0' } }),
      FETCH_TIMEOUT_MS,
      `DuckDuckGo search timed out after ${FETCH_TIMEOUT_MS}ms`,
    )
    const text = await res.text()
    if (!res.ok) {
      logger.warn(
        { status: res.status, body: text.slice(0, 500), query },
        'Direct web search (DuckDuckGo) request failed',
      )
      return {
        error: `DuckDuckGo search failed: ${res.status} ${text.slice(0, 200)}`,
      }
    }
    const json = tryParseJson(text)
    if (!json || typeof json !== 'object') {
      return { error: 'DuckDuckGo search returned an invalid response' }
    }

    const results: Array<{ title?: string; link?: string; snippet?: string }> =
      []

    const heading = getStringField(json, 'Heading')
    const abstractText = getStringField(json, 'AbstractText')
    const abstractUrl = getStringField(json, 'AbstractURL')
    if (abstractText && heading) {
      results.push({ title: heading, link: abstractUrl, snippet: abstractText })
    } else if (abstractText) {
      results.push({ title: query, link: abstractUrl, snippet: abstractText })
    }

    const answer = getStringField(json, 'Answer')
    if (answer && !abstractText) {
      results.push({ title: query, snippet: answer })
    }

    // RelatedTopics is either a flat array of {Text, FirstURL} or an array of
    // category groups {Name, Topics: [...]}. Flatten both.
    const related = (json as Record<string, unknown>).RelatedTopics
    if (Array.isArray(related)) {
      for (const item of related) {
        if (!item || typeof item !== 'object') continue
        const rec = item as Record<string, unknown>
        const nested = rec.Topics
        if (Array.isArray(nested)) {
          for (const sub of nested) {
            if (!sub || typeof sub !== 'object') continue
            const s = sub as Record<string, unknown>
            results.push({
              title: getStringField(s, 'Text'),
              link: getStringField(s, 'FirstURL'),
            })
          }
        } else {
          results.push({
            title: getStringField(rec, 'Text'),
            link: getStringField(rec, 'FirstURL'),
          })
        }
      }
    }

    // Deep depth: include the separate Results block too.
    if (depth === 'deep') {
      const extra = (json as Record<string, unknown>).Results
      if (Array.isArray(extra)) {
        for (const item of extra) {
          if (!item || typeof item !== 'object') continue
          const rec = item as Record<string, unknown>
          results.push({
            title: getStringField(rec, 'Text'),
            link: getStringField(rec, 'FirstURL'),
          })
        }
      }
    }

    const result = formatSearchResults(results)
    if (!result) {
      return {
        error:
          `No search results found for "${query}". ` +
          'Tip: set SERPER_API_KEY in .env.local for full Google search results.',
      }
    }
    return { result }
  } catch (error) {
    logger.error(
      {
        error:
          error instanceof Error
            ? { name: error.name, message: error.message }
            : error,
        query,
      },
      'Direct web search (DuckDuckGo) network error',
    )
    return { error: error instanceof Error ? error.message : 'Network error' }
  }
}

export async function directWebSearch(params: {
  query: string
  depth?: 'standard' | 'deep'
  fetch: typeof globalThis.fetch
  logger: Logger
}): Promise<{ result?: string; error?: string; creditsUsed?: number }> {
  const { query, depth = 'standard', fetch, logger } = params

  const serperApiKey = process.env.SERPER_API_KEY
  if (serperApiKey) {
    const serperResult = await searchWithSerper({
      query,
      apiKey: serperApiKey,
      depth,
      fetch,
      logger,
    })
    if (!serperResult.error) return { result: serperResult.result }
    // Fall through to DuckDuckGo if Serper fails (key expired, quota, etc.).
    logger.warn(
      { serperError: serperResult.error, query },
      'Serper search failed; falling back to DuckDuckGo',
    )
  }

  return searchWithDuckDuckGo({ query, depth, fetch, logger })
}

// ---------------------------------------------------------------------------
// read_docs
// ---------------------------------------------------------------------------

export async function directDocsSearch(params: {
  libraryTitle: string
  topic?: string
  maxTokens?: number
  fetch: typeof globalThis.fetch
  logger: Logger
}): Promise<{ documentation?: string; error?: string; creditsUsed?: number }> {
  const { libraryTitle, topic, maxTokens, fetch, logger } = params

  const documentation = await fetchContext7LibraryDocumentation({
    query: libraryTitle,
    topic,
    tokens: maxTokens,
    fetch,
    logger,
  })

  if (!documentation) {
    return {
      error: `No documentation found for "${libraryTitle}"${
        topic ? ` (topic: ${topic})` : ''
      }. Context7 only covers public libraries; small or private libraries may not be available.`,
    }
  }
  return { documentation }
}
